import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import RatingBadge from "@/components/base/RatingBadge";
import MovieCard from "@/components/feature/MovieCard";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);

  useDocumentMeta(
    movie ? `${movie.title} - 详情 - 国产在线观看` : "资源不存在或已下架 - 国产在线观看",
    movie
      ? `${movie.title}，国产高清影视作品介绍，剧情、主演、评分一览，尽在国产在线观看。`
      : "该影视资源不存在或已下架，返回国产在线观看首页浏览更多国产影视作品。"
  );

  useEffect(() => {
    setExpanded(false);
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2200);
  };

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.type === movie.type || m.region === movie.region || m.year === movie.year)
      )
      .sort((a, b) => b.heat - a.heat)
      .slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-movie-2-line text-5xl text-foreground-400" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          资源不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          你访问的影视资源可能已被移除，返回首页看看其他精彩国产影视作品吧。
        </p>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    const now = toggleFavorite(movie.id);
    showToast(now ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: movie.title, text: movie.synopsis, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("分享失败，请手动复制地址");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <Link to={`/category/${movie.type}`} className="transition-colors hover:text-primary-600">
          {movie.type}
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-800">{movie.title}</span>
      </nav>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="mx-auto w-[180px] shrink-0 overflow-hidden rounded-lg bg-background-200 sm:mx-0 sm:w-[220px]">
            <div className="aspect-[2/3] w-full">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 国产影视作品`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <RatingBadge rating={movie.rating} size="md" />
              <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                {movie.type}
              </span>
              {movie.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900"
                >
                  {tag}
                </span>
              ))}
            </div>

            <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">上映年份</dt>
                <dd className="text-foreground-900">{movie.year}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">地区</dt>
                <dd className="text-foreground-900">{movie.region}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">导演</dt>
                <dd className="text-foreground-900">{movie.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">更新时间</dt>
                <dd className="text-foreground-900">{movie.updatedAt}</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="shrink-0 text-foreground-500">主演</dt>
                <dd className="text-foreground-900">{movie.actors.join(" / ")}</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="shrink-0 text-foreground-500">类型</dt>
                <dd className="text-foreground-900">{movie.genres.join(" / ")}</dd>
              </div>
            </dl>

            <p className="mt-4 text-sm leading-relaxed text-foreground-600">{movie.synopsis}</p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} aria-hidden="true" />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-line text-base" aria-hidden="true" />
                分享
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line text-base" aria-hidden="true" />
                返回上一页
              </button>
              <Link
                to="/"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-home-4-line text-base" aria-hidden="true" />
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
        <p
          className={`mt-3 text-sm leading-relaxed text-foreground-700 ${
            expanded ? "" : "line-clamp-3"
          }`}
        >
          {movie.description}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          {expanded ? "收起" : "展开全文"}
          <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
        </button>
      </section>

      <div className="mt-6">
        <EpisodeList movie={movie} />
      </div>

      {related.length > 0 && (
        <section className="mt-10" aria-labelledby="related-title">
          <h2
            id="related-title"
            className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950"
          >
            <i className="ri-movie-2-line text-xl text-primary-500" aria-hidden="true" />
            相关推荐
          </h2>
          <p className="mt-1 text-sm text-foreground-500">同类型、同地区或同年度精选作品</p>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}